// ============================================================================
// FSA POLICY NOTE — TipTap rich-text editor (controlled allow-list)
// ============================================================================
//
// Thin React wrapper around @tiptap/react with the explicit extension
// allow-list that POLICY notes use:
//   · paragraphs + text
//   · bold / italic / underline marks
//   · bullet + ordered lists
//   · tables (insert / delete row / column, header row toggle — no
//     merge / split / cell styling)
//
// Returns the document as TipTap JSON via onChange. Save layer
// stores the doc verbatim into workspace.policy_section_data; render
// layer applies its own allow-list at print time.

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  useEditor,
  useEditorState,
  EditorContent,
  Node,
  mergeAttributes,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type Editor,
  type NodeViewProps,
} from '@tiptap/react';
import type { ReportFieldOption, ReportTable } from 'api/types/fsa/reportData.types';
import DataInsertPicker from './DataInsertPicker';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TextAlign } from '@tiptap/extension-text-align';
import IconSystem from 'icon-collection/icon-systems';
import { fsaDocumentOutputProfileService } from 'api/services/fsa/fsaDocumentOutputProfileService';
import type { BodyAlign } from 'api/types/fsa/documentOutput.types';

// ---------------------------------------------------------------------------
// Inject editor styles once per module load. TipTap ships zero CSS for
// tables / lists / paragraphs, so the editor renders cells as plain text
// with no borders — auditors can't see the table structure while editing.
// Scoped to `.ProseMirror.tiptap-fsa-editor` so it never bleeds into
// other content. Mirrors the conventional ProseMirror table styling
// the TipTap team publishes.
// ---------------------------------------------------------------------------
const EDITOR_STYLE_ID = 'fsa-tiptap-editor-styles';
if (typeof document !== 'undefined' && !document.getElementById(EDITOR_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = EDITOR_STYLE_ID;
  style.textContent = `
.ProseMirror.tiptap-fsa-editor {
  outline: none;
}
.ProseMirror.tiptap-fsa-editor > * + * {
  margin-top: 0.5em;
}
.ProseMirror.tiptap-fsa-editor p { margin: 0; }
.ProseMirror.tiptap-fsa-editor ul,
.ProseMirror.tiptap-fsa-editor ol {
  padding-left: 1.25rem;
  margin: 0.25rem 0;
}
.ProseMirror.tiptap-fsa-editor li > p { margin: 0; }

/* ADR-0024 — house-style body alignment (Document Output Settings), so the
   editor shows what prints. A paragraph's own alignment is an inline style
   and wins; table cells never justify (the PDF doesn't either). */
.fsa-body-justify .ProseMirror.tiptap-fsa-editor p { text-align: justify; }
.fsa-body-justify .ProseMirror.tiptap-fsa-editor table p { text-align: left; }

/* Tables — visible borders, header row distinction, selected-cell hint */
.ProseMirror.tiptap-fsa-editor table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 0.5rem 0;
  overflow: hidden;
  border: 1px solid #94a3b8;
}
.ProseMirror.tiptap-fsa-editor table td,
.ProseMirror.tiptap-fsa-editor table th {
  min-width: 2em;
  border: 1px solid #cbd5e1;
  padding: 4px 6px;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}
.ProseMirror.tiptap-fsa-editor table th {
  font-weight: 600;
  text-align: left;
  background-color: #eef3f9;
  border-bottom: 1px solid #94a3b8;
}
/* TipTap marks the active cell with .selectedCell — give it a faint tint
   so the auditor always knows which cell the cursor is in. */
.ProseMirror.tiptap-fsa-editor table .selectedCell {
  background-color: rgba(37, 99, 235, 0.08);
}
.ProseMirror.tiptap-fsa-editor table .selectedCell::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: rgba(37, 99, 235, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.45);
}
/* When a column / row is being resized — TipTap adds .column-resize-handle */
.ProseMirror.tiptap-fsa-editor table .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: #2563eb;
  pointer-events: none;
}
.ProseMirror.tiptap-fsa-editor table p { margin: 0; }

/* ADR-0024 — live field chip (value resolved from engagement data) */
.ProseMirror.tiptap-fsa-editor .fsa-field-chip {
  display: inline-block;
  padding: 0 6px;
  margin: 0 1px;
  border-radius: 4px;
  background: #e0f2fe;
  color: #075985;
  border: 1px solid #bae6fd;
  font-size: 0.82em;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
}
.ProseMirror.tiptap-fsa-editor .fsa-field-chip.ProseMirror-selectednode {
  outline: 2px solid #0284c7;
}
/* ADR-0024 — data table block (rows built from engagement data) */
.ProseMirror.tiptap-fsa-editor .fsa-data-table {
  margin: 0.5rem 0;
  padding: 10px 12px;
  border: 1px dashed #7dd3fc;
  border-radius: 6px;
  background: #f0f9ff;
  color: #075985;
  font-size: 0.82em;
  font-weight: 600;
}
.ProseMirror.tiptap-fsa-editor .fsa-data-table.ProseMirror-selectednode {
  outline: 2px solid #0284c7;
}
.ProseMirror.tiptap-fsa-editor .fsa-field-chip[data-unresolved="true"] {
  background: #fef3c7;
  color: #92400e;
  border-color: #fcd34d;
}
.ProseMirror.tiptap-fsa-editor .fsa-data-table-caption {
  margin-bottom: 6px;
}
.ProseMirror.tiptap-fsa-editor .fsa-data-table-caption span,
.ProseMirror.tiptap-fsa-editor .fsa-data-table-note {
  font-weight: 400;
  font-style: italic;
  color: #0369a1;
}
.ProseMirror.tiptap-fsa-editor .fsa-data-table table {
  margin: 0;
  background: white;
  color: var(--text-primary, #0f172a);
  font-weight: 400;
}
`;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// ADR-0024 — data-bound nodes. Both are atoms: the auditor can move or delete
// them but not type inside them. The value (chip) / rows (table) come from the
// report workspace SNAPSHOT (resolved_fields / resolved_tables), supplied via
// ReportDataContext by the host pane — saved-as-primary: the editor shows what
// will print. Without a provider (POLICY notes) chips show their label.
// ---------------------------------------------------------------------------

export interface ReportDataContextValue {
  fields: Record<string, string>;
  tables: Record<string, ReportTable>;
}

export const ReportDataContext = createContext<ReportDataContextValue | null>(null);

const FieldChipView: React.FC<NodeViewProps> = ({ node, selected }) => {
  const ctx = useContext(ReportDataContext);
  const path = node.attrs.path as string | null;
  const label = (node.attrs.label as string | null) ?? path ?? 'field';
  const value = ctx && path ? ctx.fields[path] : undefined;
  const unresolved = ctx != null && (value == null || value === '');
  return (
    <NodeViewWrapper
      as="span"
      className="fsa-field-chip"
      data-unresolved={unresolved ? 'true' : undefined}
      title={`${label}${path ? ` — ${path}` : ''}${unresolved ? ' — no value yet' : ''}`}
      style={selected ? { outline: '2px solid #0284c7' } : undefined}
    >
      {unresolved ? `⚠ ${label}` : (value ?? label)}
    </NodeViewWrapper>
  );
};

const DataTableView: React.FC<NodeViewProps> = ({ node, selected }) => {
  const ctx = useContext(ReportDataContext);
  const source = node.attrs.source as string | null;
  const label = (node.attrs.label as string | null) ?? source ?? 'Table';
  const table = ctx && source ? ctx.tables[source] : undefined;
  return (
    <NodeViewWrapper
      className="fsa-data-table"
      contentEditable={false}
      style={selected ? { outline: '2px solid #0284c7' } : undefined}
    >
      <div className="fsa-data-table-caption">▦ {label} <span>· built from engagement data</span></div>
      {!table ? (
        <div className="fsa-data-table-note">Shown when the report is opened from an engagement.</div>
      ) : table.rows.length === 0 ? (
        table.emptyText
          ? <div style={{ fontWeight: 400, color: 'var(--text-primary, #0f172a)' }}>{table.emptyText}</div>
          : <div className="fsa-data-table-note">{table.emptyNote ?? 'No rows.'}</div>
      ) : (
        <table>
          <thead>
            <tr>{table.columns.map((c, i) => <th key={i} style={{ textAlign: (table.align[i] as any) ?? 'left' }}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {table.rows.map((r, ri) => (
              <tr key={ri}>{r.map((cell, ci) => (
                <td key={ci} style={{ textAlign: (table.align[ci] as any) ?? 'left', fontWeight: table.boldRows?.includes(ri) ? 600 : undefined }}>{cell}</td>
              ))}</tr>
            ))}
          </tbody>
        </table>
      )}
      {table?.footnote && <div className="fsa-data-table-note">{table.footnote}</div>}
    </NodeViewWrapper>
  );
};

/** Inline field, e.g. { path: "fs.PL.PL_PROFIT_FOR_PERIOD.cy", label: "Profit for the year (CY)" }. */
export const FieldChip = Node.create({
  name: 'fieldChip',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      path: {
        default: null,
        parseHTML: el => el.getAttribute('data-field-path'),
        renderHTML: attrs => ({ 'data-field-path': attrs.path }),
      },
      label: {
        default: null,
        parseHTML: el => el.getAttribute('data-field-label'),
        renderHTML: attrs => ({ 'data-field-label': attrs.label }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-field-path]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { class: 'fsa-field-chip', title: node.attrs.path ?? '' }),
      node.attrs.label ?? node.attrs.path ?? 'field',
    ];
  },
  renderText({ node }) {
    return `[${node.attrs.label ?? node.attrs.path ?? 'field'}]`;
  },
  addNodeView() {
    return ReactNodeViewRenderer(FieldChipView);
  },
});

/** Block table whose rows come from engagement data, e.g. { source: "attendance", label: "Attendance" }. */
export const DataTable = Node.create({
  name: 'dataTable',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      source: {
        default: null,
        parseHTML: el => el.getAttribute('data-table-source'),
        renderHTML: attrs => ({ 'data-table-source': attrs.source }),
      },
      label: {
        default: null,
        parseHTML: el => el.getAttribute('data-table-label'),
        renderHTML: attrs => ({ 'data-table-label': attrs.label }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-table-source]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: 'fsa-data-table' }),
      `▦ ${node.attrs.label ?? node.attrs.source ?? 'Table'} — built from engagement data`,
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(DataTableView);
  },
});

interface Props {
  /** Initial document (TipTap JSON). Undefined → empty paragraph. */
  initialContent?: unknown;
  /**
   * Called on every change with the document JSON. Use this to push
   * into workspace.policy_section_data[<code>].content.
   */
  onChange: (doc: unknown) => void;
  /** Disabled (read-only) when true. */
  readOnly?: boolean;
  /**
   * Key changes force a remount — pass section code so switching
   * sections wipes editor state cleanly. `initialContent` is read ONLY
   * at creation, so the key must also change whenever the content is
   * replaced from outside the editor (saved workspace loaded, restore
   * default) — see PolicyEditorPane's editorKey.
   */
  sectionKey?: string;
  /**
   * ADR-0024 — when supplied, the toolbar offers "Insert field" (a live
   * fieldChip) and "Insert data table" pickers. Omitted for POLICY notes.
   */
  dataInsertOptions?: DataInsertOptions;
  /**
   * Fill the parent's height: the formatting bar stays at the top and only
   * the text scrolls (the document editor). Without it the editor sizes to
   * its content.
   */
  fill?: boolean;
}

export interface DataInsertOptions {
  fields: ReportFieldOption[];
  tables: Array<{ source: string; label: string }>;
}

const TiptapEditor: React.FC<Props> = ({ initialContent, onChange, readOnly = false, sectionKey, dataInsertOptions, fill = false }) => {
  const editor = useEditor({
    extensions: [
      // StarterKit ships paragraph, bold, italic, lists, history,
      // hard-break, etc. We disable codeBlock / blockquote / heading /
      // horizontalRule / strike — keep the policy-note surface tight.
      StarterKit.configure({
        codeBlock: false,
        blockquote: false,
        heading: false,
        horizontalRule: false,
        strike: false,
        code: false,
      }),
      Underline,
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
      FieldChip,
      DataTable,
      // ADR-0024 — per-paragraph alignment override. No default: a paragraph
      // without textAlign follows the house style from Document Output
      // Settings; only an explicit choice is stored.
      TextAlign.configure({
        types: ['paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
        defaultAlignment: null,
      }),
    ],
    content: initialContent ?? { type: 'doc', content: [{ type: 'paragraph' }] },
    editable: !readOnly,
    editorProps: {
      // Tag the ProseMirror root so the scoped table / list styles
      // injected above attach without bleeding into other rich-text
      // surfaces that may be added later.
      attributes: { class: 'tiptap-fsa-editor' },
    },
    onUpdate: ({ editor: e }) => {
      onChange(e.getJSON());
    },
    // Section-keyed identity — remount on section switch
    // (see useEditor immutableProps behaviour).
  }, [sectionKey]);

  // Sync editable when readOnly prop toggles mid-life.
  useEffect(() => {
    if (!editor) return;
    if (editor.isEditable !== !readOnly) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  const houseAlign = useHouseBodyAlign();

  if (!editor) {
    return (
      <div style={{ padding: '1rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
        Loading editor…
      </div>
    );
  }

  return (
    <div style={fill ? { ...editorWrapStyle, flex: 1, minHeight: 0 } : editorWrapStyle} className={houseAlign === 'JUSTIFY' ? 'fsa-body-justify' : undefined}>
      {!readOnly && <Toolbar editor={editor} dataInsertOptions={dataInsertOptions} houseAlign={houseAlign} />}
      <EditorContent editor={editor} style={fill ? { ...editorContentStyle, minHeight: 0 } : editorContentStyle} />
    </div>
  );
};

/** House body-text alignment (cached per session by the service). LEFT until known. */
function useHouseBodyAlign(): BodyAlign {
  const [align, setAlign] = useState<BodyAlign>('LEFT');
  useEffect(() => {
    let alive = true;
    fsaDocumentOutputProfileService.getBodyAlign().then(a => { if (alive) setAlign(a); });
    return () => { alive = false; };
  }, []);
  return align;
}

type ParaAlign = 'left' | 'center' | 'right' | 'justify';

/** Small line-glyph icons for the alignment buttons. */
const AlignIcon: React.FC<{ align: ParaAlign }> = ({ align }) => {
  // Four lines; the short ones show the alignment.
  const rows: Array<[number, number]> = align === 'justify'
    ? [[1, 13], [1, 13], [1, 13], [1, 13]]
    : align === 'left' ? [[1, 13], [1, 9], [1, 13], [1, 8]]
    : align === 'right' ? [[1, 13], [5, 13], [1, 13], [6, 13]]
    : [[1, 13], [3, 11], [1, 13], [4, 10]];
  return (
    <svg width={14} height={14} viewBox="0 0 14 14" aria-hidden="true">
      {rows.map(([x1, x2], i) => (
        <line key={i} x1={x1} x2={x2} y1={2.5 + i * 3} y2={2.5 + i * 3} stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" />
      ))}
    </svg>
  );
};

const ALIGN_BUTTONS: Array<{ align: ParaAlign; label: string }> = [
  { align: 'left', label: 'Align left' },
  { align: 'center', label: 'Centre' },
  { align: 'right', label: 'Align right' },
  { align: 'justify', label: 'Justify' },
];

// ============================================================================
// TOOLBAR
// ============================================================================

const Toolbar: React.FC<{ editor: Editor; dataInsertOptions?: DataInsertOptions; houseAlign: BodyAlign }> = ({ editor, dataInsertOptions, houseAlign }) => {
  // TipTap v3's useEditor does not re-render on selection / transaction
  // changes, so reading editor.isActive() straight off `editor` goes stale
  // (clicking into a table cell never enabled the Row / Column buttons).
  // Subscribe to exactly the state the toolbar paints.
  const { inTable, bold, italic, underline, bulletList, orderedList, explicitAlign } = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      inTable: e.isActive('table'),
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      bulletList: e.isActive('bulletList'),
      orderedList: e.isActive('orderedList'),
      // The paragraph's explicit alignment, or null = following the house style.
      explicitAlign: ALIGN_BUTTONS.find(b => e.isActive({ textAlign: b.align }))?.align ?? null,
    }),
  });
  const houseLabel = houseAlign === 'JUSTIFY' ? 'justified' : 'left aligned';
  const effectiveAlign: ParaAlign = explicitAlign ?? (houseAlign === 'JUSTIFY' ? 'justify' : 'left');

  // Wrap each command so it focuses the editor first. Useful because the
  // toolbar button receives focus on click — without `.focus()` first,
  // TipTap commands fire against a stale selection (or skip silently
  // when the cursor wasn't in a table when the button was clicked).
  const cmd = (fn: () => void) => () => { editor.commands.focus(); fn(); };

  return (
    <div style={toolbarColumnStyle}>
      {/* Main toolbar — text formatting, lists, insert-table */}
      <div style={toolbarStyle}>
        <ToolbarButton
          active={bold}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold (Ctrl+B)"
        >
          <strong style={{ fontSize: '0.85rem' }}>B</strong>
        </ToolbarButton>
        <ToolbarButton
          active={italic}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic (Ctrl+I)"
        >
          <em style={{ fontSize: '0.85rem', fontWeight: 500 }}>I</em>
        </ToolbarButton>
        <ToolbarButton
          active={underline}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline (Ctrl+U)"
        >
          <span style={{ fontSize: '0.85rem', textDecoration: 'underline' }}>U</span>
        </ToolbarButton>

        <Divider />

        <ToolbarButton
          active={bulletList}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet list"
        >
          <IconSystem.list size={14} />
        </ToolbarButton>
        <ToolbarButton
          active={orderedList}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered list"
        >
          <span style={{ fontWeight: 600, fontSize: '0.7rem' }}>1.</span>
        </ToolbarButton>

        <Divider />

        {/* ADR-0024 — paragraph alignment. Clicking the paragraph's own
            alignment again clears it back to the house style. Disabled in
            tables: cells always print unjustified. */}
        {ALIGN_BUTTONS.map(b => (
          <ToolbarButton
            key={b.align}
            active={!inTable && effectiveAlign === b.align}
            disabled={inTable}
            onClick={() => {
              if (explicitAlign === b.align) editor.chain().focus().unsetTextAlign().run();
              else editor.chain().focus().setTextAlign(b.align).run();
            }}
            title={inTable
              ? 'Alignment applies to paragraphs outside tables'
              : explicitAlign === b.align
                ? `${b.label} — set on this paragraph; click to follow the house style (${houseLabel})`
                : explicitAlign == null && effectiveAlign === b.align
                  ? `${b.label} — house style from Document Output Settings`
                  : `${b.label} this paragraph`}
          >
            <AlignIcon align={b.align} />
          </ToolbarButton>
        ))}

        <Divider />

        <ToolbarButton
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          title="Insert a new 3-row × 3-column table"
        >
          <IconSystem.grid size={14} />
          <span style={{ marginLeft: 4, fontSize: '0.72rem' }}>Insert table</span>
        </ToolbarButton>

        {dataInsertOptions && (
          <>
            <Divider />
            <DataInsertPicker
              fields={dataInsertOptions.fields}
              tables={dataInsertOptions.tables}
              onInsertField={f => editor.chain().focus()
                .insertContent({ type: 'fieldChip', attrs: { path: f.path, label: f.label } }).run()}
              onInsertTable={t => editor.chain().focus()
                .insertContent({ type: 'dataTable', attrs: { source: t.source, label: t.label } }).run()}
            />
          </>
        )}
      </div>

      {/* Table sub-toolbar — shown only while the cursor is in a table
          ("Insert table" in the main bar says tables exist); outside one it
          only took the text's height. */}
      {inTable && (
      <div style={tableToolbarStyle}>
        <span style={tableGroupLabelStyle}>Row:</span>
        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().addRowBefore().run())}
          disabled={!inTable}
          title={inTable ? 'Add row above the current row' : 'Click inside a table cell first'}
          label="+ Above"
        />
        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().addRowAfter().run())}
          disabled={!inTable}
          title={inTable ? 'Add row below the current row' : 'Click inside a table cell first'}
          label="+ Below"
        />
        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().deleteRow().run())}
          disabled={!inTable}
          title={inTable ? 'Delete the current row' : 'Click inside a table cell first'}
          label="− Delete"
          danger
        />

        <Divider />

        <span style={tableGroupLabelStyle}>Column:</span>
        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().addColumnBefore().run())}
          disabled={!inTable}
          title={inTable ? 'Add column to the left' : 'Click inside a table cell first'}
          label="+ Left"
        />
        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().addColumnAfter().run())}
          disabled={!inTable}
          title={inTable ? 'Add column to the right' : 'Click inside a table cell first'}
          label="+ Right"
        />
        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().deleteColumn().run())}
          disabled={!inTable}
          title={inTable ? 'Delete the current column' : 'Click inside a table cell first'}
          label="− Delete"
          danger
        />

        <Divider />

        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().toggleHeaderRow().run())}
          disabled={!inTable}
          title={inTable ? 'Toggle whether the top row is the table header' : 'Click inside a table cell first'}
          label="Header row"
        />
        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().deleteTable().run())}
          disabled={!inTable}
          title={inTable ? 'Delete the entire table' : 'Click inside a table cell first'}
          label="Delete table"
          danger
        />

      </div>
      )}
    </div>
  );
};

const LabeledToolbarButton: React.FC<{
  onClick: () => void;
  title: string;
  label: string;
  danger?: boolean;
  disabled?: boolean;
}> = ({ onClick, title, label, danger, disabled }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      ...labeledBtnStyle,
      color: disabled ? 'var(--text-muted, #94a3b8)' : (danger ? '#dc2626' : 'var(--text-primary)'),
      borderColor: disabled ? 'var(--border-color)' : (danger ? 'rgba(220, 38, 38, 0.35)' : 'var(--border-color)'),
      cursor: disabled ? 'not-allowed' : 'pointer',
      background: disabled ? 'transparent' : 'var(--bg-primary)',
    }}
  >
    {label}
  </button>
);

interface ToolbarButtonProps {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ active, disabled, onClick, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      ...toolbarBtnStyle,
      background: active ? 'var(--bg-secondary)' : 'transparent',
      color: disabled ? 'var(--text-muted, #94a3b8)' : active ? 'var(--text-primary)' : 'var(--text-secondary)',
      fontWeight: active ? 600 : 400,
      cursor: disabled ? 'not-allowed' : 'pointer',
    }}
  >
    {children}
  </button>
);

const Divider: React.FC = () => (
  <div style={{
    width: 1, height: 18,
    background: 'var(--border-color)',
    margin: '0 4px',
  }} />
);

// ============================================================================
// STYLES
// ============================================================================

const editorWrapStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column',
  border: '1px solid var(--form-border-color)',
  borderRadius: 4,
  overflow: 'hidden',
  background: 'var(--bg-primary)',
};
const toolbarColumnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};
const toolbarStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center',
  gap: 2,
  padding: '4px 6px',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};
const tableToolbarStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center',
  gap: 4,
  padding: '4px 6px',
  borderBottom: '1px solid var(--border-color)',
  background: '#eef3f9',
};
const tableGroupLabelStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 600,
  color: '#1e3a5f',
  marginLeft: 4,
  marginRight: 2,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};
const toolbarBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: 26, height: 26,
  padding: '0 6px',
  border: 'none', borderRadius: 3,
  cursor: 'pointer',
  fontSize: '0.85rem',
};
const labeledBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  height: 24,
  padding: '0 10px',
  border: '1px solid var(--border-color)',
  borderRadius: 4,
  background: 'var(--bg-primary)',
  cursor: 'pointer',
  fontSize: '0.72rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
};
const editorContentStyle: React.CSSProperties = {
  flex: 1,
  padding: '8px 12px',
  fontSize: '0.875rem',
  lineHeight: 1.5,
  color: 'var(--text-primary)',
  minHeight: 220,
  overflow: 'auto',
};

export default TiptapEditor;
